import React, { useRef, useState, useEffect } from "react";
import { View, TextInput, StyleSheet, StyleProp, ViewStyle, TextStyle, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import useContextMenu from "@/hooks/useContextMenu";
import useDateTime from "@/hooks/useDateTime";
import { DateTimePicker } from "@/components/Pickers/DatetimePicker";
import { parse, isValid } from "date-fns";
import { parseFlexibleDate } from "@/lib/dateParser";
import { Skeleton } from "@/components/ui/Skeleton";

export type DateInputProps = {
  label?: string;
  value?: Date | string | null;
  placeholder?: string;
  mode?: "date" | "datetime";
  onChange: (date: Date) => void;
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;
  editing?: boolean;
  testID?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  /** External error message (e.g., "Start time cannot be in the past") */
  error?: string;
  /** Show skeleton loading state */
  loading?: boolean;
  /** Custom style for the label */
  labelStyle?: StyleProp<TextStyle>;
};

export function DateInput({
  label,
  value,
  placeholder,
  mode = "datetime",
  onChange,
  style,
  disabled = false,
  editing = true,
  testID,
  minDate,
  maxDate,
  error,
  loading = false,
  labelStyle,
}: DateInputProps) {
  const { colors } = useTheme();
  const { formatDate, formatTimestamp, settings, datePlaceholder, datetimePlaceholder } = useDateTime();

  // Use format-based placeholder if none provided
  const effectivePlaceholder = placeholder ?? (mode === "date" ? datePlaceholder : datetimePlaceholder);
  const contextMenu = useContextMenu();
  const buttonRef = useRef<View>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Parse value to Date
  const dateValue = value ? (typeof value === "string" ? new Date(value) : value) : undefined;

  // Format for display/editing
  const formatForDisplay = (date: Date | undefined) => {
    if (!date) return "";
    return mode === "date" ? formatDate(date) : formatTimestamp(date, true);
  };

  const [textValue, setTextValue] = useState(formatForDisplay(dateValue));

  // Sync text value when external value changes
  useEffect(() => {
    setTextValue(formatForDisplay(dateValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Reset when menu closes externally
  useEffect(() => {
    if (!contextMenu.visible && isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu.visible]);

  const handleOpenPicker = () => {
    if (disabled || !editing) return;

    buttonRef.current?.measureInWindow((x, y, w, h) => {
      setIsOpen(true);
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x: x - 260, y: y + (h ?? 0) + 4 });
      contextMenu.setMenuContent(
        <DateTimePicker
          date={dateValue}
          hideTime={mode === "date"}
          fireOnDayPick={mode === "date"}
          minDate={minDate}
          maxDate={maxDate}
          onPick={(e) => {
            onChange(e.date);
            setTextValue(formatForDisplay(e.date));
            contextMenu.setVisible(false);
            setIsOpen(false);
          }}
          onCancel={() => {
            contextMenu.setVisible(false);
            setIsOpen(false);
          }}
        />,
      );
      contextMenu.setVisible(true);
    });
  };

  // Check if a date is within the allowed range
  const isDateInRange = (d: Date): boolean => {
    if (minDate) {
      const min = typeof minDate === "string" ? new Date(minDate) : minDate;
      const minStart = new Date(min.getFullYear(), min.getMonth(), min.getDate(), 0, 0, 0, 0);
      if (d < minStart) return false;
    }
    if (maxDate) {
      const max = typeof maxDate === "string" ? new Date(maxDate) : maxDate;
      const maxEnd = new Date(max.getFullYear(), max.getMonth(), max.getDate(), 23, 59, 59, 999);
      if (d > maxEnd) return false;
    }
    return true;
  };

  // Parse user input on blur
  const handleBlur = () => {
    if (!textValue.trim()) return;

    // Try flexible parsing first (handles various formats)
    const flexParsed = parseFlexibleDate(textValue, {
      mode,
      baseDate: dateValue || new Date(),
    });

    if (flexParsed && isValid(flexParsed)) {
      if (isDateInRange(flexParsed)) {
        onChange(flexParsed);
        setTextValue(formatForDisplay(flexParsed));
      } else {
        // Reset to original value if out of range
        setTextValue(formatForDisplay(dateValue));
      }
      return;
    }

    // Fallback: try strict format parsing
    const pattern =
      mode === "date"
        ? settings.dateFormat
        : `${settings.dateFormat} ${settings.timeFormat === "24" ? "HH:mm" : "h:mm a"}`;

    const parsed = parse(textValue, pattern, new Date());
    if (isValid(parsed) && isDateInRange(parsed)) {
      onChange(parsed);
      setTextValue(formatForDisplay(parsed));
    } else {
      // Reset to original value if invalid or out of range
      setTextValue(formatForDisplay(dateValue));
    }
  };

  const showButton = editing && !disabled;
  const hasError = !!error;

  // Loading state - show skeleton
  if (loading) {
    return (
      <View style={[styles.container, style]}>
        {label && <ThemedText style={[styles.label, labelStyle]}>{label}</ThemedText>}
        <View style={styles.row}>
          <Skeleton width="100%" height={40} radius={8} style={{ flex: 1 }} />
          {showButton && <Skeleton width={40} height={40} radius={8} />}
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      {(label || hasError) && (
        <View style={styles.labelRow}>
          {label && <ThemedText style={[styles.label, labelStyle]}>{label}</ThemedText>}
          {hasError && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText || colors.ruby?.text || "red" }]}>
              {error}
            </ThemedText>
          )}
        </View>
      )}
      <View style={styles.row}>
        <TextInput
          style={[
            styles.input,
            {
              color: colors.text,
              borderColor: hasError ? colors.ruby?.border || "red" : colors.border,
              backgroundColor: colors.backgroundSecondary,
              opacity: disabled ? 0.6 : 1,
            },
          ]}
          value={textValue}
          onChangeText={setTextValue}
          onBlur={handleBlur}
          placeholder={effectivePlaceholder}
          placeholderTextColor={colors.placeholder}
          editable={editing && !disabled}
          testID={testID}
        />
        {showButton && (
          <Button
            ref={buttonRef}
            icon="calendar"
            iconColor="white"
            style={[styles.button, { backgroundColor: colors.primary }]}
            onPress={handleOpenPicker}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  errorText: {
    fontSize: 12,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  input: {
    flex: 1,
    height: 40,
    padding: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  button: {
    width: 40,
    height: 40,
    paddingHorizontal: 0,
  },
});
